import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BiometricProvider } from './contexts/BiometricContext';

// Public pages
import PublicLayout        from './components/Public/PublicLayout';
import Home                from './components/Public/Home';
import About               from './components/Public/About';
import Features            from './components/Public/Features';
import Contact             from './components/Public/Contact';

// Auth
import Login from './components/Auth/Login';

// Admin pages
import AdminLayout          from './components/Layout/AdminLayout';
import Dashboard            from './components/Dashboard/Dashboard';
import Consumers            from './components/Consumers/Consumers';
import ConsumerForm         from './components/Consumers/ConsumerForm';
import ConsumerDetails      from './components/Consumers/ConsumerDetails';
import Attendance           from './components/Attendance/Attendance';
import BiometricAttendance  from './components/Attendance/BiometricAttendance';
import Measurements         from './components/Measurements/Measurements';
import Payments             from './components/Payments/Payments';
import Centers              from './components/Centers/Centers';
import Coaches              from './components/Coaches/Coaches';
import Profile              from './components/Profile/Profile';
import SubscriptionManagement from './components/Subscriptions/SubscriptionManagement';

import './App.css';

/* ── Redirect unauthenticated users to /login ── */
const RequireAuth = ({ children }) => {
  const { user, loading } = useAuth();
  
  console.log("🛡️ RequireAuth check:", { loading, hasUser: !!user });
  
  // Wait for auth check to complete
  if (loading) {
    console.log("⏳ RequireAuth: Still loading, showing spinner...");
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: '#f1f5f9'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: 48,
            marginBottom: 16,
            animation: 'spin 1s linear infinite'
          }}>⚙️</div>
          <div style={{ color: '#64748b', fontSize: 14 }}>Loading...</div>
        </div>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }
  
  if (!user) {
    console.log("❌ RequireAuth: No user, redirecting to /login");
    return <Navigate to="/login" replace />;
  }
  
  console.log("✅ RequireAuth: User authenticated, rendering protected content");
  return children;
};

/* ── Login page — redirect based on role ── */
const LoginPage = () => {
  const { user } = useAuth();
  if (user) {
    // Super admins go to dashboard, center admins go to attendance
    const redirectPath = user.role === 'super_admin' ? '/dashboard' : '/attendance';
    return <Navigate to={redirectPath} replace />;
  }
  return <Login />;
};

/* ── Root app ── */
function App() {
  console.log("🚀 App component rendering");
  return (
    <AuthProvider>
      <BiometricProvider>
        <Router>
          <div className="App">
            <Routes>

              {/* ── Public website ── */}
              <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
              <Route path="/about"    element={<PublicLayout><About /></PublicLayout>} />
              <Route path="/features" element={<PublicLayout><Features /></PublicLayout>} />
              <Route path="/contact"  element={<PublicLayout><Contact /></PublicLayout>} />

              {/* ── Auth ── */}
              <Route path="/login" element={<LoginPage />} />

              {/* ── Admin panel (protected) ── */}
              <Route path="/dashboard" element={
                <RequireAuth><AdminLayout><Dashboard /></AdminLayout></RequireAuth>
              } />
              <Route path="/profile" element={
                <RequireAuth><AdminLayout><Profile /></AdminLayout></RequireAuth>
              } />
              <Route path="/attendance" element={
                <RequireAuth><AdminLayout><Attendance /></AdminLayout></RequireAuth>
              } />
              <Route path="/biometric-attendance" element={
                <RequireAuth><AdminLayout><BiometricAttendance /></AdminLayout></RequireAuth>
              } />
              <Route path="/consumers" element={
                <RequireAuth><AdminLayout><Consumers /></AdminLayout></RequireAuth>
              } />
              <Route path="/consumers/new" element={
                <RequireAuth><AdminLayout><ConsumerForm /></AdminLayout></RequireAuth>
              } />
              <Route path="/consumers/:id" element={
                <RequireAuth><AdminLayout><ConsumerDetails /></AdminLayout></RequireAuth>
              } />
              <Route path="/consumers/:id/edit" element={
                <RequireAuth><AdminLayout><ConsumerForm /></AdminLayout></RequireAuth>
              } />
              <Route path="/measurements" element={
                <RequireAuth><AdminLayout><Measurements /></AdminLayout></RequireAuth>
              } />
              <Route path="/payments" element={
                <RequireAuth><AdminLayout><Payments /></AdminLayout></RequireAuth>
              } />
              <Route path="/centers" element={
                <RequireAuth><AdminLayout><Centers /></AdminLayout></RequireAuth>
              } />
              <Route path="/subscriptions" element={
                <RequireAuth><AdminLayout><SubscriptionManagement /></AdminLayout></RequireAuth>
              } />
              <Route path="/coaches" element={
                <RequireAuth><AdminLayout><Coaches /></AdminLayout></RequireAuth>
              } />

              {/* ── Fallback ── */}
              <Route path="*" element={<Navigate to="/" replace />} />

            </Routes>
          </div>
        </Router>
      </BiometricProvider>
    </AuthProvider>
  );
}

export default App;
